/**
 * Why a magic link did not sign the visitor in.
 *
 * The gateway classifies a failed link against its verification row — the only
 * point where an expired token is still distinguishable from an unknown one —
 * and sends the browser to `/login?reason=...` carrying one of these. The
 * provider's own error codes never reach the browser, so this is a closed set:
 * an unrecognised value shows nothing rather than being echoed.
 */
export type SignInFailureReason = "link-expired" | "link-invalid" | "sign-in-failed";

export interface SignInNotice {
  title: string;
  message: string;
}

const SIGN_IN_NOTICES: Record<SignInFailureReason, SignInNotice> = {
  "link-expired": {
    title: "That sign-in link has expired",
    message: "Enter your email to request a new link.",
  },
  "link-invalid": {
    title: "That sign-in link didn't work",
    message: "Open your newest sign-in email or request a new link.",
  },
  "sign-in-failed": {
    title: "We couldn't sign you in",
    message:
      "That link can no longer sign you in. " +
      "Enter your email for a new one, or contact your administrator.",
  },
};

/**
 * The notice a sign-in page should show for a location's query, or `undefined`
 * when there is nothing to say. Only `reason` is read: a caller's own callback
 * URL may legitimately carry `error`, and that is the provider's vocabulary,
 * not a message for this page.
 */
export function parseSignInNotice(search: string): SignInNotice | undefined {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const reason = params.get("reason");
  // `hasOwn`, so an inherited name like `constructor` reads as unrecognised
  // rather than as a notice.
  if (!reason || !Object.hasOwn(SIGN_IN_NOTICES, reason)) return undefined;
  return SIGN_IN_NOTICES[reason as SignInFailureReason];
}
